import { Component, inject, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { take } from "rxjs";
import { RecoverPassStep, RecoverUIConfig } from "./recover-password.vm";
import {
  PASSWORD_RECOVERY_PORT,
  PasswordRecoveryPort,
  isValidEmail,
} from "lib-klab-core";

@Component({
  selector: "klab-auth-recover-password",
  templateUrl: "./recover-password.component.html",
  styleUrls: ["./recover-password.component.scss"],
  standalone: false,
})
export class RecoverPasswordPageComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly recoveryPort = inject(
    PASSWORD_RECOVERY_PORT,
  ) as PasswordRecoveryPort;

  form!: FormGroup;

  step: RecoverPassStep = "email";
  loading = false;

  maskedEmail: string | null = null;

  showPasswordMismatch = false;

  uiConfig: Record<RecoverPassStep, RecoverUIConfig> = {
    email: {
      title: "¿Olvidaste tu contraseña?",
      subtitle: "¡No te preocupes!",
      description:
        "Ingresa el correo electrónico de tu cuenta y te enviaremos las instrucciones para restablecer tu contraseña.",
      buttonLabel: "Continuar",
      showTerms: true,
    },
    code: {
      title: "¿Olvidaste tu contraseña?",
      subtitle: "Revise su correo",
      description:
        "Por favor, ingresa el código que enviamos a tu correo {email}.",
      buttonLabel: "Verificar",
      showTerms: true,
    },
    validating: {
      title: "¿Olvidaste tu contraseña?",
      subtitle: "Revisa tu correo",
      description:
        "Por favor, ingresa el código que enviamos a tu correo {email}.",
      buttonLabel: "Verificar",
      showTerms: true,
    },
    newPassword: {
      title: "Restablecer contraseña",
      subtitle: "¡No te preocupes!",
      description:
        "Crea una nueva contraseña para recuperar el acceso a tu cuenta.",
      buttonLabel: "Continuar",
      showTerms: false,
    },
    success: {
      title: "Contraseña actualizada correctamente",
      subtitle: "",
      description:
        "Tu nueva contraseña ya está activada. Puedes iniciar sesión nuevamente.",
      buttonLabel: "Ir al Login",
      showTerms: false,
    },
  };

  private emailFormatValidator = (control: {
    value?: string | null;
  }): { email: true } | null => {
    const v = control?.value;
    if (v == null || String(v).trim() === "") return null;
    return isValidEmail(v) ? null : { email: true };
  };

  ngOnInit(): void {
    this.form = new FormGroup({
      username: new FormControl("", [
        Validators.required,
        this.emailFormatValidator,
      ]),
      code: new FormControl("", [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6),
      ]),
      newPassword: new FormControl("", [
        Validators.required,
        Validators.minLength(8),
      ]),
      confirmPassword: new FormControl("", [
        Validators.required,
        Validators.minLength(8),
      ]),
    });

    this.form.get("newPassword")?.valueChanges.subscribe(() => {
      this.showPasswordMismatch = false;
    });

    this.form.get("confirmPassword")?.valueChanges.subscribe(() => {
      this.showPasswordMismatch = false;
    });
  }

  get isPrimaryButtonDisabled(): boolean {
    if (this.loading) return true;
    switch (this.step) {
      case "email":
        return this.form.get("username")?.invalid ?? true;

      case "code":
        return this.form.get("code")?.invalid ?? true;

      case "newPassword": {
        const newPass = this.form.get("newPassword")?.value || "";
        const confirmPass = this.form.get("confirmPassword")?.value || "";
        const minOk = newPass.length >= 8 && confirmPass.length >= 8;
        return !minOk;
      }

      case "validating":
        return true;

      case "success":
        return false;

      default:
        return true;
    }
  }

  handleSubmitStep(): void {
    switch (this.step) {
      case "email":
        this.submitEmail();
        break;
      case "code":
        this.submitCode();
        break;
      case "newPassword":
        this.submitNewPassword();
        break;
      case "success":
        this.goToLogin();
        break;
    }
  }

  private submitEmail(): void {
    const control = this.form.get("username");
    if (!control || control.invalid) return;

    const email = control.value as string;
    this.maskedEmail = this.maskEmail(email);
    this.loading = true;

    this.recoveryPort
      .requestRecoveryCode({ email, tokenType: "PASSWORD_RECOVERY" })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
          this.step = "code";
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private maskEmail(email: string): string {
    const [localPart, domain] = email.split("@");
    if (!domain) return email;

    if (localPart.length <= 2) {
      return localPart[0] + "***@" + domain;
    }

    const firstTwo = localPart.slice(0, 2);
    const lastTwo = localPart.slice(-2);
    return `${firstTwo}******${lastTwo}@${domain}`;
  }

  private submitCode(): void {
    const codeValue = this.form.get("code")?.value;
    if (!codeValue) return;

    const email = this.form.get("username")?.value as string;
    this.loading = true;
    this.step = "validating";

    this.recoveryPort
      .validateRecoveryCode({ email, token: codeValue })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
          this.step = "newPassword";
        },
        error: () => {
          this.loading = false;
          this.step = "code";
        },
      });
  }

  private submitNewPassword(): void {
    const newPass = this.form.get("newPassword")?.value || "";
    const confirmPass = this.form.get("confirmPassword")?.value || "";

    const minOk = newPass.length >= 8 && confirmPass.length >= 8;
    if (!minOk) {
      this.showPasswordMismatch = false;
      return;
    }

    if (newPass !== confirmPass) {
      this.showPasswordMismatch = true;
      this.form.get("confirmPassword")?.markAsTouched();
      return;
    }

    this.showPasswordMismatch = false;
    this.loading = true;

    const email = this.form.get("username")?.value as string;

    this.recoveryPort
      .resetPassword({
        email,
        newPassword: newPass,
        confirmPassword: confirmPass,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.loading = false;
          this.step = "success";
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  goToLogin(): void {
    this.router.navigateByUrl("/login");
  }

  handleResendCode(): void {
    const email = this.form.get("username")?.value as string;
    if (!email) return;

    this.recoveryPort
      .requestRecoveryCode({ email, tokenType: "PASSWORD_RECOVERY" })
      .pipe(take(1))
      .subscribe();
  }

  get currentDescription(): string {
    const base = this.uiConfig[this.step].description || "";
    if (!this.maskedEmail) {
      return base.replace("{email}", "");
    }
    return base.replace("{email}", this.maskedEmail);
  }
}
